import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import {

  getSessionId,

  getSessionIdFromUrl,

  pathWithSessionId,

  initializeSession,

} from '../utils/sessionManager';

import { getApiBaseUrl } from '../utils/apiConfig';

import { saveSupplementalProfile } from '../utils/dataSaver';

import { detectSkinBatch } from '../utils/skinAnalysisApi';

import { fetchAllowedConditionNames } from '../utils/segmentation';

import { getPreviousSkinAnalysis } from '../utils/previousSkinAnalysis';

import {

  normalizeDetections,

  normalizeDescriptionMap,

  normalizeSkinScore,

} from '../utils/skinConditionNormalization';

import {

  REQUEST_STATUS,

  createRequestScope,

  isAbortError,

  isTimeoutError,

  logSkinResilience,

} from '../utils/skinResilience';

import AISkinLoader from '../components/AISkinLoader';

import PageFooter from '../components/PageFooter';

import { useSkinSession } from '../context/SkinSessionContext';

import { useSkinAnalysisPlan } from '../context/SkinAnalysisPlanContext';

import './AISkinAnalysisPage.css';

import '../styles/PageBackground.css';

import '../styles/ActionButtons.css';



const CONF_THRESHOLD_MESSAGE = 'Processing already in progress. Please wait.';



function statusMessage(status, detectProgress) {

  if (detectProgress?.message) return detectProgress.message;

  switch (status) {

    case REQUEST_STATUS.RETRYING:

      return 'Connection interrupted — retrying analysis…';

    case REQUEST_STATUS.RECONNECTING:

      return 'Reconnecting to server…';

    case REQUEST_STATUS.QUEUED:

      return 'Analysis is already in progress. Please wait…';

    case REQUEST_STATUS.TIMEOUT:

      return 'Analysis timed out. You can try again.';

    default:

      return 'Preparing analysis…';

  }

}



function useResolvedSession(location, token) {

  const [sessionId, setSessionId] = useState(() => {

    const fromState = location.state?.sessionId;

    const fromUrl = getSessionIdFromUrl(location);

    const fromStorage = getSessionId();

    return fromState ?? fromUrl ?? fromStorage ?? null;

  });

  const apiBaseUrl = getApiBaseUrl();



  useEffect(() => {

    const fromState = location.state?.sessionId;

    const fromUrl = getSessionIdFromUrl(location);

    const fromStorage = getSessionId();

    const resolved = fromState || fromUrl || fromStorage;



    if (resolved && sessionId !== resolved) {

      setSessionId(resolved);

      return;

    }

    if (sessionId) return;



    const init = async () => {

      const sid = await initializeSession(apiBaseUrl, token ? { app_session_token: token } : {});

      setSessionId(sid);

    };

    init();

  }, [apiBaseUrl, sessionId, location, token]);



  return [sessionId, setSessionId];

}



const AISkinAnalysisPage = () => {

  const navigate = useNavigate();

  const location = useLocation();

  const { token, sessionLandingPath } = useSkinSession();

  const {

    ENABLE_RADAR_CHART,

    ENABLE_BEFORE_RADAR_CHART,

  } = useSkinAnalysisPlan();



  const capturedImage = location.state?.capturedImage ?? null;

  const sideCaptures = location.state?.sideCaptures ?? null;

  const userDataFromState = location.state?.userData ?? null;

  const rawSkinGoalData = location.state?.skinGoalData;

  const skinGoalDataFromState = useMemo(() => rawSkinGoalData ?? { goals: [] }, [rawSkinGoalData]);

  const aqiDataFromState = location.state?.aqiData ?? null;



  const [sessionId, setSessionId] = useResolvedSession(location, token);

  const [isDetecting, setIsDetecting] = useState(false);

  const [error, setError] = useState(null);

  const [requestStatus, setRequestStatus] = useState(REQUEST_STATUS.IDLE);

  const [detectProgress, setDetectProgress] = useState(null);



  const hasDetectedRef = useRef(false);

  const detectionInProgressRef = useRef(false);

  const retryCountRef = useRef(0);

  const manualRetryBusyRef = useRef(false);

  const requestScopeRef = useRef(null);



  const abortActiveRequest = useCallback((reason) => {

    if (requestScopeRef.current) {

      logSkinResilience('abort', { label: 'detect-skin-page', reason });

      requestScopeRef.current.abort(reason);

      requestScopeRef.current = null;

    }

  }, []);



  useEffect(() => () => abortActiveRequest('unmount'), [abortActiveRequest]);



  const navigateToRoutine = useCallback(

    (skinResults, finalSessionId, aiConditionDescriptions = null, previousSkinData = null) => {

      abortActiveRequest('navigate');

      const path = pathWithSessionId('/skin-routine', finalSessionId);

      navigate(path, {

        state: {

          capturedImage,

          sideCaptures: sideCaptures || undefined,

          userData: userDataFromState,

          sessionId: finalSessionId,

          skinResults,

          skinGoalData: skinGoalDataFromState || { goals: [] },

          skinGoalsSkipped: true,

          aqiData: aqiDataFromState,

          aiConditionDescriptions: aiConditionDescriptions ?? null,

          previousSkinData: previousSkinData ?? null,

          ...(token && { token }),

        },

        replace: true,

      });

    },

    [navigate, capturedImage, sideCaptures, userDataFromState, skinGoalDataFromState, aqiDataFromState, token, abortActiveRequest]

  );



  const landmarksFromState = location.state?.landmarks ?? null;



  const runDetection = useCallback(

    async (imageDataUrl, { isManualRetry = false } = {}) => {

      if (

        !imageDataUrl ||

        isDetecting ||

        !sessionId ||

        hasDetectedRef.current ||

        detectionInProgressRef.current ||

        (isManualRetry && manualRetryBusyRef.current)

      ) {

        return;

      }



      if (isManualRetry) manualRetryBusyRef.current = true;

      abortActiveRequest('retry-replacement');

      const scope = createRequestScope();

      requestScopeRef.current = scope;



      hasDetectedRef.current = true;

      detectionInProgressRef.current = true;

      setIsDetecting(true);

      setError(null);

      setRequestStatus(REQUEST_STATUS.PROCESSING);

      setDetectProgress({ phase: 'starting', message: 'Preparing analysis…', batchSize: 1 });



      const currentSessionId =

        sessionId ?? location.state?.sessionId ?? getSessionId();

      const userAge = userDataFromState?.age ?? null;

      const landmarks = (() => {

        const fromState = landmarksFromState;

        if (fromState && Array.isArray(fromState) && fromState.length > 0) return fromState;

        try {

          const raw = sessionStorage.getItem(`nyafaz_skin_landmarks_${currentSessionId}`);

          if (!raw) return null;

          const parsed = JSON.parse(raw);

          return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;

        } catch {

          return null;

        }

      })();



      const finishWithError = (message, status = REQUEST_STATUS.RETRY_AVAILABLE) => {

        hasDetectedRef.current = false;

        detectionInProgressRef.current = false;

        setError(message);

        setRequestStatus(status);

      };



      try {

        let allowedConditions = null;

        if (token) {

          const allowedSet = await fetchAllowedConditionNames(token);

          if (scope.isAborted()) return;

          if (allowedSet) allowedConditions = Array.from(allowedSet);

        }



        const previousPromise =

          ENABLE_RADAR_CHART && ENABLE_BEFORE_RADAR_CHART

            ? getPreviousSkinAnalysis(currentSessionId, token)

            : Promise.resolve({ success: true, previous: null });



        const detectParams = {

          sessionId: currentSessionId,

          age: userAge,

          allowedConditions,

          appSessionToken: token || null,

          landmarks,

          signal: scope.signal,

          onRetry: ({ reconnecting, timeout }) => {

            setRequestStatus(

              reconnecting || timeout ? REQUEST_STATUS.RECONNECTING : REQUEST_STATUS.RETRYING

            );

            setDetectProgress((prev) => ({

              ...(prev || {}),

              phase: 'retry',

              message: reconnecting

                ? 'Reconnecting to server…'

                : 'Retrying analysis…',

            }));

          },

        };



        const data = await detectSkinBatch({

          frontImage: imageDataUrl,

          sideCaptures: sideCaptures || undefined,

          retryCount: retryCountRef.current,

          onProgress: setDetectProgress,

          ...detectParams,

        });



        if (scope.isAborted()) return;



        if (!data.success || !data.marked_image) {

          const persistFail = data?.persistence_failed;

          finishWithError(

            data.error || (persistFail ? 'Failed to save analysis results. Please try again.' : 'Detection failed'),

            persistFail

              ? (data?.retryable === false ? REQUEST_STATUS.FAILED : REQUEST_STATUS.RETRY_AVAILABLE)

              : REQUEST_STATUS.FAILED

          );

          return;

        }

        if (data.persistence_verified !== true) {

          finishWithError(

            'Analysis completed but persistence was not verified. Please try again.',

            REQUEST_STATUS.RETRY_AVAILABLE

          );

          return;

        }



        const detectionsList = normalizeDetections(data.detections ?? []);

        const frontDetections = normalizeDetections(data.front_detections ?? data.detections ?? []);

        const finalSkinConditions = data.skin_conditions ?? null;

        const finalSkinScore = normalizeSkinScore(data.skin_score ?? null);

        const hadSessionFromState = !!(sessionId ?? location.state?.sessionId);

        const finalSessionId = hadSessionFromState

          ? currentSessionId

          : (data.session_id || currentSessionId);



        if (data.session_id && !hadSessionFromState) {

          setSessionId(data.session_id);

        }



        setDetectProgress({ phase: 'persist', message: 'Saving profile details…', batchSize: 1 });

        const saveResult = await saveSupplementalProfile(

          finalSessionId,

          userDataFromState ?? {},

          token ?? null

        );

        if (scope.isAborted()) return;

        if (!saveResult?.success && !saveResult?.skipped) {

          finishWithError(

            saveResult?.error || 'Failed to save profile details. Please try again.',

            saveResult?.retryable === false ? REQUEST_STATUS.FAILED : REQUEST_STATUS.RETRY_AVAILABLE

          );

          return;

        }



        const aiDescriptions =

          data?.ai_condition_descriptions && typeof data.ai_condition_descriptions === 'object'

            ? normalizeDescriptionMap(data.ai_condition_descriptions)

            : {};



        const previousRes = await previousPromise;

        if (scope.isAborted()) return;

        const previousSkinData =

          previousRes.success && previousRes.previous ? previousRes.previous : null;



        setRequestStatus(REQUEST_STATUS.IDLE);

        navigateToRoutine(

          {

            detections: detectionsList,

            frontDetections,

            skinScore: finalSkinScore,

            skinConditions: finalSkinConditions,

            markedImage: data.marked_image,

            sideResults: data.side_results || null,

          },

          finalSessionId,

          aiDescriptions,

          previousSkinData

        );

      } catch (err) {

        if (isAbortError(err)) return;



        if (err.status === 409) {

          finishWithError(err.apiResponse?.error || CONF_THRESHOLD_MESSAGE, REQUEST_STATUS.QUEUED);

          return;

        }

        if (isTimeoutError(err)) {

          finishWithError(

            'Analysis timed out. The server may still be processing — try again in a moment.',

            REQUEST_STATUS.TIMEOUT

          );

          return;

        }

        if (err.status === 503 || err.retryable) {

          finishWithError(

            err.apiResponse?.error || err.message || 'Processing interrupted. Please try again.',

            REQUEST_STATUS.RETRY_AVAILABLE

          );

          return;

        }

        finishWithError(err.message || 'Failed to detect skin conditions', REQUEST_STATUS.FAILED);

      } finally {

        manualRetryBusyRef.current = false;

        detectionInProgressRef.current = false;

        setIsDetecting(false);

        setDetectProgress(null);

        if (requestScopeRef.current === scope) {

          requestScopeRef.current = null;

        }

      }

    },

    [

      isDetecting,

      sessionId,

      setSessionId,

      location.state?.sessionId,

      landmarksFromState,

      userDataFromState,

      token,

      sideCaptures,

      ENABLE_RADAR_CHART,

      ENABLE_BEFORE_RADAR_CHART,

      navigateToRoutine,

      abortActiveRequest,

    ]

  );



  useEffect(() => {

    if (

      capturedImage &&

      !isDetecting &&

      sessionId &&

      !hasDetectedRef.current &&

      !detectionInProgressRef.current

    ) {

      runDetection(capturedImage);

    }

  }, [capturedImage, sessionId, runDetection, isDetecting]);



  const handleBack = useCallback(() => {

    abortActiveRequest('route-change');

    const sid = sessionId ?? getSessionIdFromUrl(location) ?? getSessionId();

    const path = pathWithSessionId('/capture', sid);

    navigate(path, {

      state: {

        capturedImage,

        userData: userDataFromState,

        sessionId: sessionId,

        aqiData: aqiDataFromState,

        ...(token && { token }),

      },

    });

  }, [navigate, sessionId, location, capturedImage, userDataFromState, aqiDataFromState, token, abortActiveRequest]);



  const handleHome = useCallback(() => {

    abortActiveRequest('route-change');

    navigate(sessionLandingPath);

  }, [navigate, sessionLandingPath, abortActiveRequest]);



  const handleRetry = useCallback(() => {

    if (manualRetryBusyRef.current || detectionInProgressRef.current) return;

    hasDetectedRef.current = false;

    retryCountRef.current += 1;

    setDetectProgress(null);

    setRequestStatus(REQUEST_STATUS.PROCESSING);

    runDetection(capturedImage, { isManualRetry: true });

  }, [runDetection, capturedImage]);



  const showLoader = !error && (isDetecting || requestStatus === REQUEST_STATUS.PROCESSING);

  const loaderMessage = statusMessage(requestStatus, detectProgress);



  if (!capturedImage) {

    return (

      <div className="ny-skin-analysis page-bg">

        <header className="ny-skin-analysis__nav" role="banner">

          <button

            type="button"

            className="ny-skin-analysis__back"

            onClick={handleBack}

            aria-label="Go back"

          >

            ←

          </button>

          <button

            type="button"

            className="ny-skin-analysis__home"

            onClick={handleHome}

            aria-label="Go to home"

          >

            <svg

              width="20"

              height="20"

              viewBox="0 0 24 24"

              fill="none"

              stroke="currentColor"

              strokeWidth="2"

              strokeLinecap="round"

              strokeLinejoin="round"

              aria-hidden="true"

            >

              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />

              <polyline points="9 22 9 12 15 12 15 22" />

            </svg>

          </button>

        </header>

        <main className="ny-skin-analysis__main">

          <div className="ny-skin-analysis__error">

            <h2>No Image Found</h2>

            <p>Please capture an image first.</p>

            <button type="button" className="skin-action-button" onClick={handleBack}>

              Go Back

            </button>

          </div>

        </main>

        <PageFooter />

      </div>

    );

  }



  return (

    <div className="ny-skin-analysis page-bg">

      <header className="ny-skin-analysis__nav" role="banner">

        <button

          type="button"

          className="ny-skin-analysis__back"

          onClick={handleBack}

          aria-label="Go back"

        >

          ←

        </button>

        <button

          type="button"

          className="ny-skin-analysis__home"

          onClick={handleHome}

          aria-label="Go to home"

        >

          <svg

            width="20"

            height="20"

            viewBox="0 0 24 24"

            fill="none"

            stroke="currentColor"

            strokeWidth="2"

            strokeLinecap="round"

            strokeLinejoin="round"

            aria-hidden="true"

          >

            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />

            <polyline points="9 22 9 12 15 12 15 22" />

          </svg>

        </button>

      </header>

      <main className="ny-skin-analysis__main">

        {error ? (

          <div className="ny-skin-analysis__error">

            <h2>{requestStatus === REQUEST_STATUS.TIMEOUT ? 'Timed Out' : 'Error'}</h2>

            <p>{error}</p>

            <button type="button" className="skin-action-button" onClick={handleRetry}>

              Try Again

            </button>

          </div>

        ) : showLoader ? (

          <AISkinLoader

            imageSrc={capturedImage}

            progressMessage={loaderMessage}
            requestStatus={requestStatus}

          />

        ) : null}

      </main>

      <PageFooter />

    </div>

  );

};



export default AISkinAnalysisPage;

